import { useReactiveVar } from '@apollo/client';
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isLoggedInVar } from '../../graphql/cache';
import HorizontalScrollTable, { TableConfigType } from '../../components/HorizontalScrollTable';
import { useWineMatrix } from '../../hooks/useWineMatrix';
import { useGetWineMatrixFilters } from '../../hooks/useGetWineMatrixFilters';
import { WineMatrix as IWineMatrix, WineMatrixData } from '../../__generated__/graphql';
import { IBgField, ISortConfig } from './types';
import { formatValue, getColorFromValue, sortDirectionArrow, ToggleButtons } from './helpers/Index';
import ServersFilter, {
  ServerSideFilterConfigType,
  ServerSideFiltersType,
} from '../../components/Filters/serversFilter';
import Typography from '../../components/Typography';

const useStyles = makeStyles(() => ({
  scrollBar: {
    overflowX: 'auto',
    position: 'fixed',
    bottom: '0px',
    left: '20px',
    width: 'calc(100vw - 20px)',
    zIndex: 1000,
    height: '16px',
    '@media (min-width: 768px) and (min-height: 750px)': {
      width: 'calc(100vw - 124px)',
      left: '104px',
    },
  },

  border: {
    border: `1px solid #D3D3D3`,
  },

  table: {
    border: `1px solid #D3D3D3`,
  },

  borderRight: {
    borderRight: `1px solid #D3D3D3`,
    borderBottom: `1px solid #D3D3D3`,
  },

  borderBottom: {
    borderBottom: `1px solid #D3D3D3`,
  },

  borderLeft: {
    borderLeft: `1px solid #D3D3D3`,
  },

  borderTop: {
    borderTop: `1px solid #D3D3D3`,
  },

  borderRightNone: {
    borderRight: '0px solid #D3D3D3',
  },
}));

const PAGE_SIZE = 30;

export default function WineMatrix(): JSX.Element {
  const { t } = useTranslation();
  const classes = useStyles();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const customScrollbarRef = useRef<HTMLDivElement>(null);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);

  const [sortConfig, setSortConfig] = useState<ISortConfig>({
    sortBy: 'wineName',
    sortDirection: 'desc',
  });

  const isLoggedIn = useReactiveVar(isLoggedInVar);
  const [from, setFrom] = useState(1);

  const [tabField, setTabField] = useState<IBgField>({
    title: '',
    value: 'wsGrade',
    minimum: 0,
    center: 5,
    maximum: 10,
  });

  const [filterConfig, setFilterConfig] = React.useState<ServerSideFilterConfigType>({
    paramsCheckRan: false,
    isFiltered: false,
    showSortResult: false,
    filters: {} as ServerSideFiltersType,
  });

  const { loading: loadingFilters, results: resultsFilters } = useGetWineMatrixFilters({
    isLoggedIn,
    filterConfig,
    tab: tabField.value,
  });
  const {
    loading: loadingWineMatrix,
    results: wineMatrixData,
    getMoreResults,
  } = useWineMatrix({
    from: 0,
    pageSize: 40,
    filterConfig,
    sortBy: sortConfig.sortBy,
    sortDirection: sortConfig.sortDirection,
    tab: tabField.value,
  });

  const table = useMemo(() => {
    const getField = (key: keyof IWineMatrix) => {
      return (wineMatrixData?.[0]?.[key] ?? 0) as number;
    };
    const start = getField('minVintage');
    const end = getField('maxVintage');

    const tradeCount = {
      max: getField('maxTradeCount'),
      min: getField('minTradeCount'),
    };

    const wsPrice = {
      max: getField('maxWs1b'),
      min: getField('minWs1b'),
    };
    const ukPriceMin = {
      max: getField('maxUk1b'),
      min: getField('minUk1b'),
    };

    const arrayLength = loadingWineMatrix ? 0 : end - start + 1;

    const vintageArray = Array.from({ length: arrayLength }, (_, index) => start + index);
    const vintageColumn: TableConfigType<WineMatrixData>[] = vintageArray.map((vintage) => {
      return {
        title: `${vintage}`,
        dataRef: 'vintage',
        value: `${vintage}`,
        columnConfig: {
          className: 'tw-p-0',
          customCellContentTemplate(_, row) {
            return (
              <div
                className={`tw-min-w-[100px]  tw-min-h-[22px] tw-flex tw-flex-row tw-justify-center  tw-items-center tw-bg-white   ${classes.borderBottom} ${classes.borderLeft} ${classes.borderTop}`}
              >
                <Typography> {row} </Typography>
              </div>
            );
          },
        },

        rowConfig: {
          className: 'tw-p-0',
          customCellContentTemplate(_, row) {
            const val = row?.[tabField.value];
            const bgValueParam = row?.[tabField.value];
            const bgValue =
              bgValueParam === null || bgValueParam === undefined || bgValueParam === ''
                ? null
                : Number(bgValueParam ?? 0);

            const colorConfig = getColorFromValue({
              value: bgValue,
              maximum: tabField.maximum,
              minimum: tabField.minimum,
              center: tabField.center,
              colorsConfig: tabField.colorsConfig,
              customColorsFunc: tabField.customColorsFunc,
            });

            // if (!val) {
            //   return (
            //     <div
            //       className={`tw-min-w-[100px] tw-min-h-[50px] tw-flex tw-flex-row tw-justify-center  tw-items-center tw-line-clamp-2 ${classes.borderBottom} ${classes.borderLeft}`}
            //     />
            //   );
            // }

            return (
              <div
                style={{
                  backgroundColor: colorConfig.bgColor,
                  color: colorConfig.textColor,
                }}
                className={`tw-min-w-[100px] tw-min-h-[50px] tw-flex tw-flex-row tw-justify-center tw-items-center tw-line-clamp-2 ${classes.borderBottom} ${classes.borderLeft}`}
              >
                <Typography>{formatValue(val as number, tabField.value)} </Typography>
              </div>
            );
          },
        },
      };
    });

    const columns: TableConfigType<WineMatrixData>[] = [
      {
        title: 'Wine Name',
        dataRef: 'wineName',
        columnConfig: {
          className: `tw-p-0 tw-sticky tw-bg-white tw-min-w-[300px] tw-left-[0px] tw-z-10`,
          customCellContentTemplate(_, row) {
            return (
              <button
                type="button"
                onClick={() => {
                  setSortConfig((prev) => ({
                    ...prev,
                    sortBy: 'wineName',
                    sortDirection: prev.sortDirection === 'asc' ? 'desc' : 'asc',
                  }));
                }}
                className={`tw-min-h-[22px] tw-flex tw-flex-row tw-min-w-[300px] tw-w-full  tw-cursor-pointer tw-items-center tw-bg-white tw-gap-2 tw-outline-none tw-px-2  ${classes.borderRightNone} ${classes.borderBottom} ${classes.borderLeft}  ${classes.borderTop}`}
              >
                <Typography> {row} </Typography>
                {sortDirectionArrow(sortConfig.sortDirection, sortConfig.sortBy)}
              </button>
            );
          },
        },
        rowConfig: {
          className: `tw-p-0 tw-sticky tw-bg-white tw-min-w-[300px] tw-left-[0px]  tw-z-10 `,
          customCellContentTemplate(_, row) {
            return (
              <div
                className={`tw-min-h-[50px] tw-flex tw-flex-row tw-items-center tw-px-2 tw-line-clamp-2 ${classes.borderBottom} ${classes.borderLeft}`}
              >
                <Typography> {row?.wineName} </Typography>
              </div>
            );
          },
        },
      },
      ...vintageColumn,
    ];

    const dataSet = wineMatrixData;

    return { dataSet, columns, tradeCount, wsPrice, ukPriceMin };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wineMatrixData, loadingWineMatrix, tabField]);

  const fetchMore = useCallback(async () => {
    const start = from * PAGE_SIZE;
    const data = await getMoreResults(start, PAGE_SIZE);
    setFrom((prev) => prev + 1);
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, getMoreResults]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current as HTMLDivElement;
    const updateSizes = () => {
      if (scrollContainer) {
        setScrollWidth(scrollContainer.scrollWidth);
        setContainerWidth(scrollContainer.clientWidth);

        scrollContainer.scrollLeft = (scrollContainer.clientWidth ?? 0) / 2;
      }
    };

    if (!loadingWineMatrix && scrollContainerRef) {
      setTimeout(() => {
        updateSizes();
      }, 2000);
    }
    window.addEventListener('resize', updateSizes);
    return () => window.removeEventListener('resize', updateSizes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingWineMatrix, scrollContainerRef, filterConfig]);

  const handleScroll = () => {
    const scrollContainer = scrollContainerRef.current as HTMLDivElement;
    const customScrollbar = customScrollbarRef.current as HTMLDivElement;
    const ratio = scrollContainer.scrollLeft / (scrollContainer.scrollWidth - scrollContainer.clientWidth);
    customScrollbar.scrollLeft = ratio * (customScrollbar.scrollWidth - customScrollbar.clientWidth);
  };

  const handleCustomScroll = () => {
    const scrollContainer = scrollContainerRef.current as HTMLDivElement;
    const customScrollbar = customScrollbarRef.current as HTMLDivElement;
    const ratio = customScrollbar.scrollLeft / (customScrollbar.scrollWidth - customScrollbar.clientWidth);
    scrollContainer.scrollLeft = ratio * (scrollContainer.scrollWidth - scrollContainer.clientWidth);
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-5 ">
      <div
        className={`tw-hidden tw-bg-white md:tw-block ${classes.scrollBar} ${loadingWineMatrix ? 'tw-invisible' : ''}`}
        ref={customScrollbarRef}
        onScroll={handleCustomScroll}
      >
        <div
          style={{
            width: `${(scrollWidth / containerWidth) * 100}%`,
            height: '100%',
            backgroundColor: 'white',
          }}
        />
      </div>

      <div className="tw-flex tw-flex-col tw-gap-5">
        <ServersFilter
          isLoading={loadingFilters}
          customFilters={{
            region: {
              label: t('common:filters.dropDownFilters.title.region'),
              selectAllText: t('common:filters.dropDownFilters.value.region'),
              selected: [],
              previewSelected: [],
              isSearchable: true,
            },

            producer: {
              label: t('common:filters.dropDownFilters.title.producer'),
              selectAllText: t('common:filters.dropDownFilters.value.producer'),
              selected: [],
              previewSelected: [],
              isSearchable: true,
            },

            score: {
              label: t('common:filters.dropDownFilters.title.score'),
              selectAllText: t('common:filters.dropDownFilters.value.score'),
              selected: [],
              previewSelected: [],
              isNumerical: true,
            },

            bottlePrice: {
              label: t('common:filters.dropDownFilters.title.bottlePrice'),
              selectAllText: t('common:filters.dropDownFilters.value.bottlePrice'),
              selected: [],
              previewSelected: [],
              isNumerical: true,
              isSearchable: true,
            },

            classification: {
              label: t('common:filters.dropDownFilters.title.classification'),
              selectAllText: t('common:filters.dropDownFilters.value.classification'),
              selected: [],
              previewSelected: [],
              isNumerical: true,
              isAsc: true,
            },

            country: {
              label: t('common:filters.dropDownFilters.title.country'),
              selectAllText: t('common:filters.dropDownFilters.value.country'),
              selected: [],
              previewSelected: [],
              isAsc: true,
            },

            liquidity: {
              label: t('common:filters.dropDownFilters.title.liquidity'),
              selectAllText: t('common:filters.dropDownFilters.value.liquidity'),
              selected: [],
              previewSelected: [],
              isNumerical: true,
              isAsc: true,
            },

            tier: {
              label: t('common:filters.dropDownFilters.title.tier'),
              selectAllText: t('common:filters.dropDownFilters.value.tier'),
              selected: [],
              previewSelected: [],
              isNumerical: true,
              isAsc: true,
            },

            wsGrade: {
              label: t('common:filters.dropDownFilters.title.wsGrade'),
              selectAllText: t('common:filters.dropDownFilters.value.wsGrade'),
              selected: [],
              previewSelected: [],
              isNumerical: true,
              isAsc: true,
            },

            ...filterConfig.filters,
          }}
          sourceData={resultsFilters}
          setFilteredSource={(isFiltered, filters) => {
            setFrom(1);
            setFilterConfig((prev) => ({
              ...prev,
              isFiltered: isFiltered,
              filters: filters as unknown as ServerSideFiltersType,
            }));
          }}
        />

        <ToggleButtons
          ukPriceMin={table.ukPriceMin}
          wsPrice={table.wsPrice}
          tradeCount={table.tradeCount}
          selected={tabField.value}
          onclick={(field) => {
            setTabField(field);
          }}
        />
      </div>
      <div
        className="tw-overflow-y-auto scrollbar-hidden  "
        style={{ zoom: 0.8 }}
        ref={scrollContainerRef}
        onScroll={handleScroll}
      >
        <HorizontalScrollTable
          isLoading={loadingWineMatrix}
          dataSetIn={table.dataSet}
          tableConfigIn={table.columns}
          fetchMore={fetchMore}
        />
      </div>
    </div>
  );
}
